import type { DataSourceDeclaration } from '../../providers/DataSourceManager.types';
import { VARIANT_TYPE, conditionalSources } from './collectFactBindings';
import type { FactBindings } from './types';

/** Binding roots that resolve from the host rather than from a data source. */
export const DECISION_RESERVED_ROOTS: ReadonlySet<string> = new Set([
  'context',
  'formState',
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * A copy of the document without what this pass itself reads: every
 * variant's `bindings` and the root's `dataSources` map. Whatever is left
 * is everything else on the page that could read a source.
 */
function withoutBindingsAndDeclarations(
  value: unknown,
  isRoot = false,
): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => withoutBindingsAndDeclarations(item));
  }
  if (!isRecord(value)) return value;
  const copy: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    if (key === 'props' && isRecord(child)) {
      const props: Record<string, unknown> = {};
      for (const [prop, propValue] of Object.entries(child)) {
        if (prop === 'bindings' && value.type === VARIANT_TYPE) continue;
        if (prop === 'dataSources' && isRoot) continue;
        props[prop] = withoutBindingsAndDeclarations(propValue);
      }
      copy[key] = props;
    } else {
      copy[key] = withoutBindingsAndDeclarations(child, key === 'root');
    }
  }
  return copy;
}

const escapeRegExp = (text: string) =>
  text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Every object key and string value in `value`, raw. Matching the raw
 * strings rather than their JSON encoding keeps a newline or tab inside a
 * template, such as `{{\n  source.path}}`, visible to the matcher.
 */
function collectStrings(value: unknown, into: string[] = []): string[] {
  if (typeof value === 'string') {
    into.push(value);
  } else if (Array.isArray(value)) {
    for (const item of value) collectStrings(item, into);
  } else if (isRecord(value)) {
    for (const [key, child] of Object.entries(value)) {
      into.push(key);
      collectStrings(child, into);
    }
  }
  return into;
}

/**
 * Gives a data source a `fetchWhen` gate when only conditional variant
 * bindings read it, so it is fetched only while one of their preconditions
 * holds. A source is left as declared when anything else could read it: an
 * unconditional binding, a `_bind` or template elsewhere on the page,
 * another source's `dependsOn`, `readyWhen`, or template, or an authored
 * `fetchWhen` of its own. Matching is by name and errs toward fetching, so
 * a page that does not use preconditions is unchanged.
 */
export function applyFetchPreconditions(
  dataSources: Record<string, DataSourceDeclaration> | null,
  factBindings: FactBindings,
  document: unknown,
): Record<string, DataSourceDeclaration> | null {
  if (!dataSources) return dataSources;
  const gates = conditionalSources(factBindings, DECISION_RESERVED_ROOTS);
  if (gates.size === 0) return dataSources;

  const page = collectStrings(withoutBindingsAndDeclarations(document));

  let gated: Record<string, DataSourceDeclaration> | null = null;
  gates.forEach((preconditions, name) => {
    const declaration = dataSources[name];
    if (!declaration || declaration.fetchWhen) return;
    const others = collectStrings(
      Object.entries(dataSources)
        .filter(([other]) => other !== name)
        .map(([, value]) => value),
    );
    const template = new RegExp(`\\{\\{\\s*${escapeRegExp(name)}(?![\\w$])`);
    const reads = (text: string) => text === name || template.test(text);
    if (page.some(reads) || others.some(reads)) return;
    gated = gated ?? { ...dataSources };
    gated[name] = { ...declaration, fetchWhen: preconditions };
  });
  return gated ?? dataSources;
}
